import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Badge } from 'primereact/badge';
import { Message } from 'primereact/message';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode } from 'primereact/api';
import { Tooltip } from 'primereact/tooltip';
import { toast } from 'react-toastify';
// import '../../../pages/BankStatementAnalysis.css';

const BankStatementOutputComponent = ({ analysisResults, loanDetails, serviceDetails, actualServiceDetails, onBackToInput, onDownloadPDF, pdfDownloadUrl, reportData, hideCredits, isSharedView }) => {
    // Hide credits if either hideCredits or isSharedView is true
    const shouldHideCredits = hideCredits || isSharedView;
    // Use actualServiceDetails first, then serviceDetails for backward compatibility
    const effectiveServiceInfo = actualServiceDetails || serviceDetails;

    // Extract remaining balance from API response
    const parsedData = typeof reportData === 'string' ? (() => {
        try { return JSON.parse(reportData); } catch(e) { return reportData; }
    })() : reportData;

    const remainingBalance = parsedData?.data?.remaining_balance || parsedData?.remaining_balance || analysisResults?.remaining_balance;
    console.log('BankStatementOutputComponent - remaining balance:', remainingBalance);
    const [selectedCard, setSelectedCard] = useState(null);
    const [showDetailView, setShowDetailView] = useState(false);
    
    // States for transaction filters
    const [globalFilter, setGlobalFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);
    const [subcategoryFilter, setSubcategoryFilter] = useState(null);
    
    // State for daily balance pagination
    const [currentPage, setCurrentPage] = useState(0);
    
    // State for EMI transactions modal
    const [showEmiTransactionsModal, setShowEmiTransactionsModal] = useState(false);
    
    // Extract loan details with defaults
    const { loan_amount = 0, interest_rate = 0, period = 0, emi = 0 } = loanDetails || {};
    
    // Log loan details for debugging
    console.log('Loan Details Received:', loanDetails);
    
    // Extract account data from analysisResults
    const accountUID = Object.keys(analysisResults?.account_summaries || {})[0];
    const accountSummary = analysisResults?.account_summaries?.[accountUID] || {};
    const monthWiseAnalysis = accountSummary.monthWiseAnalysis || {};
    
    // Calculate key metrics from actual data
    const avgBalance = monthWiseAnalysis.monthlyAvgBal?.average || 0;
    const totalCredits = monthWiseAnalysis.credits?.totalValue || 0;
    const totalDebits = monthWiseAnalysis.debits?.totalValue || 0;
    // Extract transactions first (before using them)
    const transactions = accountSummary.transactions || [];
    const incomes = accountSummary.incomes || [];
    const expenses = accountSummary.expenses || [];
    
    const salaryData = monthWiseAnalysis.salary || {};
    const salaryTransactions = salaryData.monthlyDetails || [];
    const totalSalaryFromJSON = salaryData.totalValue || 0;
    const avgSalaryFromJSON = salaryData.average || 0;
    
    // Calculate total salary from individual transactions
    const salaryIncomes = incomes.filter(inc => inc.isSalary) || [];
    const totalSalaryCalculated = salaryIncomes.reduce((sum, inc) => sum + (inc.amount || 0), 0);
    const avgSalaryCalculated = salaryIncomes.length > 0 ? totalSalaryCalculated / salaryIncomes.length : 0;
    
    // Use calculated values if available, otherwise fall back to JSON values
    // You can switch this logic based on your preference
    const totalSalary = totalSalaryCalculated > 0 ? totalSalaryCalculated : totalSalaryFromJSON;
    const avgSalary = avgSalaryCalculated > 0 ? avgSalaryCalculated : avgSalaryFromJSON;
    
    // Log the comparison for debugging
    console.log('Salary Calculation Comparison:', {
        fromJSON: { total: totalSalaryFromJSON, avg: avgSalaryFromJSON },
        calculated: { total: totalSalaryCalculated, avg: avgSalaryCalculated, count: salaryIncomes.length },
        using: { total: totalSalary, avg: avgSalary }
    });
    
    // Calculate EMI to ABB ratio - use safe defaults
    const emiToAbbRatio = emi > 0 && avgBalance > 0 ? (emi / avgBalance * 100).toFixed(1) : 0;
    const allEmiTotal = monthWiseAnalysis['ECS/NACH']?.totalValue || 0;
    const allEmiToAbbRatio = allEmiTotal && avgBalance ? (allEmiTotal / avgBalance * 100).toFixed(1) : 0;
    
    // Extract NACH transactions for tab header
    const nachTransactions = monthWiseAnalysis['ECS/NACH'] || {};
    
    // Format currency
    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '₹0';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Calculate salary consistency logic
    const calculateSalaryConsistency = () => {
        if (salaryTransactions.length < 3) {
            return {
                isConsistent: false,
                reason: 'Less than 3 months of salary data',
                details: 'Need at least 3 months of salary credits to determine consistency',
                variance: 0,
                monthGaps: []
            };
        }
        
        // Calculate variance in salary amounts
        const salaryAmounts = salaryTransactions.map(t => t.value || 0);
        const avgSalaryAmount = salaryAmounts.reduce((a, b) => a + b, 0) / salaryAmounts.length;
        const variance = salaryAmounts.map(s => Math.abs(s - avgSalaryAmount) / avgSalaryAmount * 100);
        const avgVariance = variance.reduce((a, b) => a + b, 0) / variance.length;
        
        // Check for missing months
        const monthGaps = [];
        for (let i = 1; i < salaryTransactions.length; i++) {
            const prevMonth = new Date(salaryTransactions[i-1].monthYear);
            const currMonth = new Date(salaryTransactions[i].monthYear);
            const monthDiff = (currMonth.getFullYear() - prevMonth.getFullYear()) * 12 + 
                            (currMonth.getMonth() - prevMonth.getMonth());
            if (monthDiff > 1) {
                monthGaps.push(`Gap of ${monthDiff - 1} months between ${salaryTransactions[i-1].monthYear} and ${salaryTransactions[i].monthYear}`);
            }
        }
        
        // Determine consistency
        if (avgVariance > 20) {
            return {
                isConsistent: false,
                reason: 'High variance in salary amounts',
                details: `Average variance: ${avgVariance.toFixed(1)}% (>20% threshold)`,
                variance: avgVariance,
                monthGaps
            };
        }
        
        if (monthGaps.length > 0) {
            return {
                isConsistent: false,
                reason: 'Irregular salary credits',
                details: monthGaps.join(', '),
                variance: avgVariance,
                monthGaps
            };
        }
        
        return {
            isConsistent: true,
            reason: 'Regular monthly salary credits',
            details: `Consistent salary with ${avgVariance.toFixed(1)}% average variance`,
            variance: avgVariance,
            monthGaps: []
        };
    };
    
    const salaryConsistency = calculateSalaryConsistency();
    
    // Calculate metrics for cards
    const calculateCardMetrics = () => {
        // B. Salary Transactions
        const isConsistentSalary = salaryConsistency.isConsistent;
        
        // C. EMI Ratio
        const emiRatioStatus = emi > 0 ? (emiToAbbRatio > 50 ? 'High Risk' : emiToAbbRatio > 30 ? 'Moderate' : 'Adequate') : 'No EMI Data';
        
        // D. Loan Transactions
        const loanCount = transactions.filter(t => 
            t.description?.toLowerCase().includes('loan') || 
            t.purpose === 'LOAN'
        ).length;
        const emiBounces = transactions.filter(t => 
            t.description?.toLowerCase().includes('bounce') &&
            (t.description?.toLowerCase().includes('emi') || t.description?.toLowerCase().includes('loan'))
        ).length;
        const emiTransactionCount = transactions.filter(t => 
            t.description?.toLowerCase().includes('emi') || 
            t.description?.toLowerCase().includes('loan') ||
            t.purpose === 'LOAN'
        ).length;
        
        // E. Share Transactions
        const investmentKeywords = ['zerodha', 'groww', 'upstox', 'kite', 'angel', 'iifl'];
        const hasShareTransactions = transactions.some(t => 
            investmentKeywords.some(keyword => 
                t.description?.toLowerCase().includes(keyword)
            )
        );
        
        // F. Online Betting
        const bettingKeywords = ['dream11', 'mpl', 'bet365', 'betting', 'fantasy'];
        const hasBettingTransactions = transactions.some(t => 
            bettingKeywords.some(keyword => 
                t.description?.toLowerCase().includes(keyword)
            )
        );
        
        // G. Fraud Analytics
        const hasFraudIndicators = false; // Simplified for now
        
        return {
            isConsistentSalary,
            salaryCount: salaryTransactions.length,
            totalSalaryAmount: totalSalary,
            emiRatio: emi > 0 && avgBalance > 0 ? (emi / avgBalance).toFixed(2) : '0',
            emiRatioStatus,
            loanCount,
            emiBounces,
            emiTransactionCount,
            hasShareTransactions,
            hasBettingTransactions,
            hasFraudIndicators
        };
    };

    const metrics = calculateCardMetrics();

    // Dashboard cards configuration
    const dashboardCards = [
        {
            id: 'A',
            title: 'Transaction Analysis',
            icon: 'pi pi-chart-line',
            color: 'blue',
            metrics: [
                { label: 'Total Credits', value: formatCurrency(totalCredits) },
                { label: 'Total Debits', value: formatCurrency(totalDebits) },
                { label: 'Avg Balance', value: formatCurrency(avgBalance) }
            ]
        },
        {
            id: 'B',
            title: 'Salary Transactions',
            icon: 'pi pi-wallet',
            color: metrics.isConsistentSalary ? 'green' : 'orange',
            metrics: [
                { label: 'Salary Credits', value: `${metrics.salaryCount} months` },
                { label: 'Net Salary/Month', value: formatCurrency(avgSalary) },
                { label: 'Total Salary', value: formatCurrency(metrics.totalSalaryAmount) },
                { label: 'Status', value: metrics.isConsistentSalary ? 'Consistent' : 'Inconsistent', isTag: true, severity: metrics.isConsistentSalary ? 'success' : 'warning' }
            ],
            extraInfo: salaryConsistency
        },
        {
            id: 'C',
            title: 'New Loan EMI to Average Bank Balance Ratio',
            icon: 'pi pi-percentage',
            color: emiToAbbRatio > 50 ? 'red' : emiToAbbRatio > 30 ? 'orange' : 'green',
            metrics: [
                { label: 'EMI to ABB', value: `${metrics.emiRatio} times` },
                { label: 'Status', value: metrics.emiRatioStatus }
            ]
        },
        {
            id: 'D',
            title: 'Loan Transactions',
            icon: 'pi pi-money-bill',
            color: metrics.emiBounces > 0 ? 'red' : 'green',
            metrics: [
                { label: 'No of Loans', value: metrics.loanCount },
                { label: 'EMI Bounces', value: metrics.emiBounces, isTag: true, severity: metrics.emiBounces > 0 ? 'danger' : 'success' },
                { label: 'No of EMI Transactions', value: metrics.emiTransactionCount || 0 }
            ]
        },
        {
            id: 'EFG',
            title: 'Risk Analysis',
            subtitle: 'Share Trading, Betting & Fraud Detection',
            icon: 'pi pi-shield',
            color: (metrics.hasBettingTransactions || metrics.hasFraudIndicators) ? 'red' : 'green',
            metrics: [
                { label: 'Share Trading', value: metrics.hasShareTransactions ? 'Yes' : 'No', isTag: true, severity: metrics.hasShareTransactions ? 'warning' : 'success' },
                { label: 'Betting Activity', value: metrics.hasBettingTransactions ? 'Yes' : 'No', isTag: true, severity: metrics.hasBettingTransactions ? 'danger' : 'success' },
                { label: 'Fraud Detected', value: metrics.hasFraudIndicators ? 'Yes' : 'No', isTag: true, severity: metrics.hasFraudIndicators ? 'danger' : 'success' }
            ]
        },
        {
            id: 'H',
            title: 'Other Analysis',
            icon: 'pi pi-info-circle',
            color: 'purple',
            metrics: [
                { label: 'Business Cash Flow', value: 'Available' },
                { label: 'Missing Documents', value: 'None' }
            ]
        }
    ];

    // Handle card click
    const handleCardClick = (card) => {
        setSelectedCard(card);
        setShowDetailView(true);
    };

    // Close detail view
    const closeDetailView = () => {
        setShowDetailView(false);
        setSelectedCard(null);
    };

    // Card color is stored as a PrimeReact color name (blue, green, orange, red, purple, indigo).
    const getColorClass = (color) => color || 'blue';

    // Render card with Diamond template classes only - Equal row heights with button at card bottom
    const renderCard = (card, index) => {
        const colorClass = getColorClass(card.color);

        return (
            <div key={card.id} className="col-12 md:col-6 lg:col-4 flex">
                <Card className="shadow-2 border-round cursor-pointer transition-all transition-duration-300 hover:shadow-4 w-full p-0">
                    <div className="flex flex-column h-full">
                        {/* Card Content - Clickable Area */}
                        <div className="flex flex-column flex-1 p-3" onClick={() => handleCardClick(card)}>
                            {/* Header with Icon */}
                            <div className="flex align-items-center gap-2 mb-3 pb-2 border-bottom-1 surface-border">
                                <div className={`flex align-items-center justify-content-center bg-${colorClass}-100 border-circle w-2rem h-2rem flex-shrink-0`}>
                                    <i className={`${card.icon} text-${colorClass}-600 text-base`}></i>
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <h6 className="m-0 text-900 font-semibold text-base white-space-nowrap text-overflow-ellipsis overflow-hidden">{card.title}</h6>
                                    {card.subtitle && <p className="text-500 text-sm m-0 mt-1 white-space-nowrap text-overflow-ellipsis overflow-hidden">{card.subtitle}</p>}
                                </div>
                            </div>

                            {/* Metrics List */}
                            <div className="flex flex-column gap-2">
                                {card.metrics.map((metric, idx) => (
                                    <div key={idx} className="surface-50 border-round p-2">
                                        <div className="flex justify-content-between align-items-center">
                                            <span className="text-600 text-sm">{metric.label}</span>
                                            {metric.isTag ? (
                                                <Tag severity={metric.severity} value={metric.value} className="text-sm" />
                                            ) : (
                                                <span className="text-900 font-bold text-base">{metric.value}</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Salary Consistency Info */}
                            {card.id === 'B' && card.extraInfo && (
                                <div className="mt-2 p-2 surface-100 border-round border-left-2 border-primary">
                                    <div className="flex align-items-start gap-2">
                                        <i className="pi pi-info-circle text-primary text-sm" style={{marginTop: '2px'}}></i>
                                        <span className="text-600 text-sm line-height-3">{card.extraInfo.details}</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* View Details Button - Always at card bottom */}
                        <div className="border-top-1 surface-border p-2" onClick={() => handleCardClick(card)}>
                            <Button
                                label="View Details"
                                icon="pi pi-arrow-right"
                                className="p-button-text p-button-sm w-full"
                                iconPos="right"
                            />
                        </div>
                    </div>
                </Card>
            </div>
        );
    };

    // Tab A: Transaction Analysis
    const renderTransactionAnalysis = () => {
        const openingClosingData = accountSummary.openingAndClosingBalance || [];
        const dailyBalances = accountSummary.dailyEODBalance || [];
        const digitalTransfer = monthWiseAnalysis.digitalTransfer || {};
        
        return (
            <div className="transaction-analysis-tab">
                {/* 1. Bank Statement Period */}
                <Panel header="Bank Statement Period & Account Details" className="mb-3">
                    <div className="info-grid">
                        <div className="info-item">
                            <label>Account Details:</label>
                            <span>{analysisResults?.upload_response?.accountList?.[0]?.accountDetails || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                            <label>Statement Period:</label>
                            <span>
                                {openingClosingData[0]?.monthAndYear || 'N/A'} to {openingClosingData[openingClosingData.length - 1]?.monthAndYear || 'N/A'}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>Opening Balance:</label>
                            <span>{formatCurrency(openingClosingData[0]?.openingBalance)}</span>
                        </div>
                        <div className="info-item">
                            <label>Closing Balance:</label>
                            <span>{formatCurrency(openingClosingData[openingClosingData.length - 1]?.closingBalance)}</span>
                        </div>
                    </div>
                </Panel>

                {/* 2. Account Summary */}
                <Panel header="Account Summary" className="mb-3">
                    <div className="summary-cards">
                        <Card className="summary-card">
                            <div className="summary-value">{formatCurrency(totalCredits)}</div>
                            <div className="summary-label">Total Credits</div>
                            <div className="summary-detail">{monthWiseAnalysis.credits?.total || 0} transactions</div>
                        </Card>
                        <Card className="summary-card">
                            <div className="summary-value">{formatCurrency(totalDebits)}</div>
                            <div className="summary-label">Total Debits</div>
                            <div className="summary-detail">{monthWiseAnalysis.debits?.total || 0} transactions</div>
                        </Card>
                        <Card className="summary-card">
                            <div className="summary-value">{formatCurrency(avgBalance)}</div>
                            <div className="summary-label">Average Balance</div>
                        </Card>
                        <Card className="summary-card">
                            <div className="summary-value">{formatCurrency(monthWiseAnalysis.maxBalance?.average)}</div>
                            <div className="summary-label">Avg Max Balance</div>
                        </Card>
                    </div>
                </Panel>

                {/* 3. Monthly Transaction Summary */}
                <Panel header="Monthly Transaction Summary" className="mb-3">
                    <DataTable value={monthWiseAnalysis.credits?.monthlyDetails || []} size="small">
                        <Column field="monthYear" header="Month" />
                        <Column header="Credits" body={(row) => {
                            const creditRow = monthWiseAnalysis.credits?.monthlyDetails?.find(c => c.monthYear === row.monthYear);
                            return `${formatCurrency(creditRow?.value || 0)} (${creditRow?.total || 0})`;
                        }} />
                        <Column header="Debits" body={(row) => {
                            const debitRow = monthWiseAnalysis.debits?.monthlyDetails?.find(d => d.monthYear === row.monthYear);
                            return `${formatCurrency(debitRow?.value || 0)} (${debitRow?.total || 0})`;
                        }} />
                        <Column header="Avg Balance" body={(row) => {
                            const balRow = monthWiseAnalysis.monthlyAvgBal?.monthlyDetails?.find(b => b.monthYear === row.monthYear);
                            return formatCurrency(balRow?.value || 0);
                        }} />
                    </DataTable>
                </Panel>

                {/* 4. UPI/Digital Transactions Analysis */}
                <Panel header="Digital Transactions Analysis" className="mb-3">
                    <div className="info-grid">
                        <div className="info-item">
                            <label>Digital Transfer Credits:</label>
                            <span>{formatCurrency(digitalTransfer.digitalTransferCredit?.totalValue || 0)}</span>
                        </div>
                        <div className="info-item">
                            <label>Digital Transfer Debits:</label>
                            <span>{formatCurrency(digitalTransfer.digitalTransferDebit?.totalValue || 0)}</span>
                        </div>
                        <div className="info-item">
                            <label>Total Digital Transactions:</label>
                            <span>{(digitalTransfer.digitalTransferDebit?.total || 0) + (digitalTransfer.digitalTransferCredit?.total || 0)}</span>
                        </div>
                    </div>
                </Panel>

                {/* 5. Daily Balances Chart */}
                <Panel header="Daily Balance Trend" className="mb-3">
                    {renderDailyBalanceChart(openingClosingData)}
                </Panel>

                {/* 6. Low ABB to EMI Ratio */}
                <Panel header="Average Bank Balance to EMI Analysis" className="mb-3">
                    <div className="ratio-analysis">
                        <div className="ratio-display">
                            <h4>ABB to New EMI Ratio</h4>
                            <div className="ratio-value">
                                <span className="big-number">{emi > 0 ? (avgBalance / emi).toFixed(2) : 'N/A'}x</span>
                                <Tag severity={emi > 0 && avgBalance / emi < 3 ? 'danger' : 'success'} 
                                     value={emi === 0 ? 'No EMI Data' : avgBalance / emi < 3 ? 'Low Balance' : 'Adequate Balance'} />
                            </div>
                            <p>Average Balance: {formatCurrency(avgBalance)} | New EMI: {emi > 0 ? formatCurrency(emi) : 'N/A'}</p>
                            {emi > 0 && avgBalance / emi < 3 && (
                                <Message severity="warn" text="Low average balance compared to EMI. May impact repayment capacity." />
                            )}
                        </div>
                    </div>
                </Panel>
            </div>
        );
    };

    // Tab B: Salary Transactions
    const renderSalaryTransactions = () => {
        const salaryDetails = incomes.filter(inc => inc.isSalary) || [];
        const isConsistent = salaryConsistency.isConsistent;
        
        // Add comprehensive null/undefined checks
        const avgSalaryValue = avgSalary || 0;
        const avgBalanceValue = avgBalance || 0;
        const salaryToAbbRatio = avgSalaryValue > 0 && avgBalanceValue > 0 ? (avgSalaryValue / avgBalanceValue).toFixed(2) : '0.00';
        
        return (
            <div className="salary-transactions-tab">
                {/* Salary Summary */}
                <Panel header="Salary Analysis Summary" className="mb-3">
                    <div className="salary-summary">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Salary Status:</label>
                                <Tag severity={isConsistent ? 'success' : 'warning'} 
                                     value={isConsistent ? 'Consistent' : 'Inconsistent'} />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', width: '100%' }}>
                                <div className="info-item">
                                    <label>Number of Salary Months:</label>
                                    <span>{salaryData.total || 0} months</span>
                                </div>
                                <div className="info-item">
                                    <label>Total Salary Credited:</label>
                                    <span>{formatCurrency(totalSalary)}</span>
                                </div>
                                <div className="info-item">
                                    <label>Net Salary Per Month:</label>
                                    <span className="font-bold text-primary">{formatCurrency(avgSalaryValue)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Salary Transaction Details */}
                <Panel header="Salary Transaction Details" className="mb-3">
                    <DataTable value={salaryDetails} size="small">
                        <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                        <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                        <Column field="description" header="Description" />
                        <Column field="bank" header="Bank" />
                        <Column header="Status" body={() => <Tag severity="success" value="Credited" />} />
                    </DataTable>
                </Panel>

                {/* Monthly Salary Breakdown */}
                <Panel header="Monthly Salary Breakdown" className="mb-3">
                    <DataTable value={salaryTransactions} size="small">
                        <Column field="monthYear" header="Month" />
                        <Column field="value" header="Salary Amount" body={(row) => formatCurrency(row.value)} />
                        <Column field="total" header="Credits" />
                        <Column header="Status" body={(row) => 
                            <Tag severity={row.value > 200000 ? 'success' : 'warning'} 
                                 value={row.value > 200000 ? 'Normal' : 'Below Average'} />
                        } />
                    </DataTable>
                </Panel>

                {/* Net Salary to ABB Ratio */}
                <Panel header="Net Salary to Average Bank Balance Ratio" className="mb-3">
                    <div className="ratio-display">
                        <h4>Salary to ABB Ratio: <strong>{salaryToAbbRatio}</strong></h4>
                        <div className="ratio-details">
                            <p>Net Salary Per Month: {formatCurrency(avgSalaryValue)}</p>
                            <p>Average Bank Balance: {formatCurrency(avgBalanceValue)}</p>
                        </div>
                        <ProgressBar value={Math.min(salaryToAbbRatio * 100, 100)}  aria-label="Progress" />
                        
                        <Divider />
                        
                        <div className="ratio-explanation mt-3">
                            <div className="flex justify-content-between align-items-center">
                            <h5>Understanding the Ratio</h5>
                            {!hideCredits && serviceDetails && (
                                <span className="text-600">
                                    Credits Deduct: {serviceDetails.credits_required || serviceDetails.credits || 0}
                                </span>
                            )}
                        </div>
                            <div className="explanation-content p-3 surface-100 border-round">
                                <p className="mb-2"><strong>Formula:</strong> Net Salary ÷ Average Bank Balance</p>
                                <p className="mb-2"><strong>Calculation:</strong> {formatCurrency(avgSalaryValue)} ÷ {formatCurrency(avgBalanceValue)} = {salaryToAbbRatio}</p>
                                <p className="mb-3"><strong>What it means:</strong></p>
                                <ul className="mb-0">
                                    <li><strong>&lt; 0.5:</strong> Good savings behavior - retaining more than 2x monthly salary</li>
                                    <li><strong>0.5 - 1.0:</strong> Moderate savings - retaining 1-2x monthly salary</li>
                                    <li><strong>&gt; 1.0:</strong> Low savings - spending more than earning or low bank balance</li>
                                </ul>
                                {salaryToAbbRatio > 1 && (
                                    <Message severity="warn" text="High ratio indicates low average balance compared to salary. Consider improving savings." className="mt-3 mb-0" />
                                )}
                                {salaryToAbbRatio < 0.5 && (
                                    <Message severity="success" text="Good financial health! Maintaining healthy bank balance relative to income." className="mt-3 mb-0" />
                                )}
                            </div>
                        </div>
                    </div>
                </Panel>
                
                {/* Salary Consistency Analysis - Separate Panel at Bottom */}
                <Panel header="Salary Consistency Analysis" className="mb-3">
                    <div className="consistency-details">
                        <div className="mb-3">
                            <div className="flex align-items-center mb-2">
                                <strong className="mr-2">Status:</strong>
                                <Tag 
                                    severity={(salaryConsistency.variance || 0) < 20 ? 'success' : 'warning'} 
                                    value={(salaryConsistency.variance || 0) < 20 ? 'Consistent' : 'High variance in salary amounts'}
                                />
                            </div>
                            <div className="mb-2">
                                <strong>Details:</strong> Average variance: {(salaryConsistency.variance || 0).toFixed(1)}% ({(salaryConsistency.variance || 0) > 20 ? '>20%' : '<20%'} threshold)
                            </div>
                            <div className="grid mb-3">
                                <div className="col-12 md:col-6">
                                    <div className="info-item">
                                        <label>Total Salary:</label>
                                        <span className="font-bold">{formatCurrency(totalSalary)}</span>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="info-item">
                                        <label>Salary Variance:</label>
                                        <span className="font-bold">{(salaryConsistency.variance || 0).toFixed(1)}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Show calculation method */}
                        <div className="mt-3 p-3 surface-100 border-round">
                            <h6 className="mb-2">How is consistency determined?</h6>
                            <ul className="mb-0">
                                <li>Requires at least 3 months of salary data</li>
                                <li>Salary variance should be less than 20%</li>
                                <li>No gaps in monthly salary credits</li>
                                <li>Regular credits on similar dates each month</li>
                            </ul>
                        </div>
                        
                        {/* Additional consistency details if available */}
                        {salaryConsistency.monthGaps && salaryConsistency.monthGaps.length > 0 && (
                            <Message 
                                severity="warn" 
                                text={`Month gaps detected: ${salaryConsistency.monthGaps.join(', ')}`} 
                                className="mt-3"
                            />
                        )}
                    </div>
                </Panel>
            </div>
        );
    };

    // Tab C: New Loan EMI Analysis
    const renderNewLoanEMIAnalysis = () => {
        const status = emi > 0 ? (emiToAbbRatio > 50 ? 'High Risk' : emiToAbbRatio > 30 ? 'Moderate Risk' : 'Low Risk') : 'No Loan Data';
        const severity = emi > 0 ? (emiToAbbRatio > 50 ? 'danger' : emiToAbbRatio > 30 ? 'warning' : 'success') : 'info';
        
        return (
            <div className="new-loan-emi-tab">
                <Panel header="New Loan EMI to Average Bank Balance Analysis" className="mb-3">
                    <div className="emi-analysis-main">
                        {/* Compact Loan Details Section */}
                        <div className="mb-4">
                            <div className="grid">
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">Proposed Loan Amount:</div>
                                    <div className="text-lg font-bold text-900">{formatCurrency(loan_amount)}</div>
                                </div>
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">Interest Rate:</div>
                                    <div className="text-lg font-bold text-900">{interest_rate}% p.a.</div>
                                </div>
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">Loan Period:</div>
                                    <div className="text-lg font-bold text-900">{period} months</div>
                                </div>
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">Proposed EMI:</div>
                                    <div className="text-lg font-bold text-primary">{formatCurrency(emi)}</div>
                                </div>
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">Average Bank Balance:</div>
                                    <div className="text-lg font-bold text-900">{formatCurrency(avgBalance)}</div>
                                </div>
                                <div className="col-12 md:col-6 lg:col-4">
                                    <div className="text-500 text-sm mb-1">EMI Coverage:</div>
                                    <div className="text-lg font-bold text-900">
                                        {avgBalance && emi ? (avgBalance / emi).toFixed(1) : '0.0'}x times
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <Divider />
                        
                        <div className="ratio-display text-center">
                            <h3>EMI to ABB Ratio</h3>
                            <div className="ratio-value">
                                <span className="big-number">{parseFloat(emiToAbbRatio).toFixed(2)}%</span>
                                <Tag severity={severity} value={status} className="ml-3" />
                            </div>
                            {emi > 0 && avgBalance > 0 && (
                                <div className="mt-3">
                                    <Card className="surface-50">
                                        <div className="flex justify-content-between align-items-center mb-3">
                                            <h5 className="text-primary m-0">
                                                <i className="pi pi-calculator mr-2"></i>
                                                EMI to ABB Ratio - Detailed Calculation & Explanation
                                            </h5>
                                            {!hideCredits && serviceDetails && (
                                                <span className="text-600">
                                                    Credits Deduct: {serviceDetails.credits_required || serviceDetails.credits || 0}
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* What is EMI to ABB Ratio */}
                                        <div className="mb-4">
                                            <h6 className="text-600 mb-2">What is EMI to ABB Ratio?</h6>
                                            <div className="surface-100 border-round p-3">
                                                <p className="m-0 text-700">
                                                    The EMI to ABB (Average Bank Balance) ratio is a critical financial metric that measures 
                                                    what percentage of your average bank balance would be consumed by the proposed loan EMI. 
                                                    It helps lenders assess your repayment capacity and financial stability.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <Divider />
                                        
                                        {/* Step by Step Calculation */}
                                        <div className="mb-4">
                                            <h6 className="text-600 mb-3">Step-by-Step Calculation</h6>
                                            
                                            <div className="timeline-style">
                                                {/* Step 1 */}
                                                <div className="flex align-items-start mb-3">
                                                    <div className="flex align-items-center justify-content-center bg-primary-100 border-circle w-2rem h-2rem mr-3 flex-shrink-0">
                                                        <span className="text-primary font-bold">1</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-700 mb-1">Identify the Proposed EMI</div>
                                                        <div className="surface-100 border-round p-2">
                                                            <code>EMI = {formatCurrency(emi)}</code>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Step 2 */}
                                                <div className="flex align-items-start mb-3">
                                                    <div className="flex align-items-center justify-content-center bg-primary-100 border-circle w-2rem h-2rem mr-3 flex-shrink-0">
                                                        <span className="text-primary font-bold">2</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-700 mb-1">Get Average Bank Balance</div>
                                                        <div className="surface-100 border-round p-2">
                                                            <code>ABB = {formatCurrency(avgBalance)}</code>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Step 3 */}
                                                <div className="flex align-items-start mb-3">
                                                    <div className="flex align-items-center justify-content-center bg-primary-100 border-circle w-2rem h-2rem mr-3 flex-shrink-0">
                                                        <span className="text-primary font-bold">3</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-semibold text-700 mb-1">Apply the Formula</div>
                                                        <div className="surface-100 border-round p-2">
                                                            <div className="text-center mb-2 p-2 surface-0 border-round">
                                                                <strong>EMI to ABB Ratio = (EMI ÷ ABB) × 100</strong>
                                                            </div>
                                                            <div className="text-sm">
                                                                = ({formatCurrency(emi)} ÷ {formatCurrency(avgBalance)}) × 100<br/>
                                                                = {(emi/avgBalance).toFixed(4)} × 100<br/>
                                                                = <strong className="text-primary">{parseFloat(emiToAbbRatio).toFixed(2)}%</strong>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <Divider />
                                        
                                        {/* Interpretation */}
                                        <div className="mb-3">
                                            <h6 className="text-600 mb-2">What Does This Mean?</h6>
                                            <div className="surface-100 border-round p-3">
                                                <ul className="m-0 pl-3">
                                                    <li className="mb-2">
                                                        Your proposed EMI is <strong className="text-primary">{parseFloat(emiToAbbRatio).toFixed(2)}%</strong> of 
                                                        your average bank balance
                                                    </li>
                                                    <li className="mb-2">
                                                        For every ₹100 in your average balance, <strong>₹{parseFloat(emiToAbbRatio).toFixed(2)}</strong> would 
                                                        be required for EMI payment
                                                    </li>
                                                    <li className="mb-2">
                                                        Your bank balance can cover the EMI <strong>{(avgBalance/emi).toFixed(1)} times</strong>
                                                    </li>
                                                    <li>
                                                        After EMI payment, you would have <strong>{formatCurrency(avgBalance - emi)}</strong> remaining 
                                                        on average
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        
                                        {/* Why This Matters */}
                                        <div>
                                            <h6 className="text-600 mb-2">Why This Ratio Matters</h6>
                                            <div className="grid">
                                                <div className="col-12 md:col-4">
                                                    <div className="text-center p-2">
                                                        <i className="pi pi-shield text-green-500 text-2xl mb-2"></i>
                                                        <div className="font-semibold text-sm">Risk Assessment</div>
                                                        <small className="text-600">Indicates ability to handle EMI</small>
                                                    </div>
                                                </div>
                                                <div className="col-12 md:col-4">
                                                    <div className="text-center p-2">
                                                        <i className="pi pi-chart-line text-blue-500 text-2xl mb-2"></i>
                                                        <div className="font-semibold text-sm">Financial Health</div>
                                                        <small className="text-600">Shows cash flow stability</small>
                                                    </div>
                                                </div>
                                                <div className="col-12 md:col-4">
                                                    <div className="text-center p-2">
                                                        <i className="pi pi-check-circle text-orange-500 text-2xl mb-2"></i>
                                                        <div className="font-semibold text-sm">Loan Eligibility</div>
                                                        <small className="text-600">Key factor in approval</small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </div>
                            )}
                            
                            <Divider />
                            
                            <div className="loan-details-comparison">
                                <div className="comparison-grid">
                                    <div className="comparison-item">
                                        <label>Proposed Loan Amount:</label>
                                        <span className="value">{loan_amount > 0 ? formatCurrency(loan_amount) : 'N/A'}</span>
                                    </div>
                                    <div className="comparison-item">
                                        <label>Interest Rate:</label>
                                        <span className="value">{interest_rate > 0 ? `${interest_rate}% p.a.` : 'N/A'}</span>
                                    </div>
                                    <div className="comparison-item">
                                        <label>Loan Period:</label>
                                        <span className="value">{period > 0 ? `${period} months` : 'N/A'}</span>
                                    </div>
                                    <div className="comparison-item">
                                        <label>Proposed EMI:</label>
                                        <span className="value">{emi > 0 ? formatCurrency(emi) : 'N/A'}</span>
                                    </div>
                                    <div className="comparison-item">
                                        <label>Average Bank Balance:</label>
                                        <span className="value">{formatCurrency(avgBalance)}</span>
                                    </div>
                                    <div className="comparison-item">
                                        <label>EMI Coverage:</label>
                                        <span className="value">{emi > 0 ? `${(avgBalance / emi).toFixed(1)}x times` : 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                            
                            <ProgressBar 
                                value={Math.min(emiToAbbRatio, 100)} 
                                color={severity === 'danger' ? 'var(--red-500)' : severity === 'warning' ? 'var(--orange-500)' : 'var(--green-500)'} 
                                className="mt-3"
                             aria-label="Progress" />
                            
                            <div className="recommendation mt-3">
                                {emi === 0 ? (
                                    <Message severity="info" text="No loan details available. Please ensure loan information is provided for EMI analysis." />
                                ) : emiToAbbRatio > 50 ? (
                                    <Message severity="error" text="High EMI burden. The proposed EMI is more than 50% of average balance. High risk of default." />
                                ) : emiToAbbRatio > 30 ? (
                                    <Message severity="warn" text="Moderate EMI burden. Manageable but requires careful financial planning." />
                                ) : (
                                    <Message severity="success" text="Low EMI burden. The proposed EMI is well within the repayment capacity." />
                                )}
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>
        );
    };

    // Tab D: Loan Transactions
    const renderLoanTransactions = () => {
        const emiTransactions = transactions.filter(t => 
            t.description?.toLowerCase().includes('emi') || 
            t.description?.toLowerCase().includes('loan') ||
            t.purpose === 'LOAN'
        ) || [];
        
        const bounceTransactions = transactions.filter(t => 
            t.description?.toLowerCase().includes('bounce') ||
            t.description?.toLowerCase().includes('return') ||
            t.description?.toLowerCase().includes('insufficient')
        ) || [];
        
        // Debug logging
        console.log('EMI Transactions:', emiTransactions);
        console.log('EMI Transactions Count:', emiTransactions.length);
        
        return (
            <div className="loan-transactions-tab">
                {/* Loan Summary */}
                <Panel header="Loan Transactions Summary" className="mb-3">
                    <div className="info-grid">
                        <div className="info-item">
                            <label>Total EMI/NACH Amount:</label>
                            <span>{formatCurrency(nachTransactions.totalValue)}</span>
                        </div>
                        <div className="info-item">
                            <label>EMI Bounces:</label>
                            <span className={bounceTransactions.length > 0 ? 'text-danger' : 'text-success'}>
                                {bounceTransactions.length}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>Number of EMI Transactions:</label>
                            <span 
                                className="cursor-pointer text-primary hover:underline"
                                onClick={() => setShowEmiTransactionsModal(true)}
                                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                title="Click to view all EMI transactions"
                            >
                                {emiTransactions.length || 0}
                            </span>
                        </div>
                        <div className="info-item">
                            <label>All EMIs to ABB Ratio:</label>
                            <span>{allEmiToAbbRatio}%</span>
                        </div>
                    </div>
                </Panel>

                {/* NACH/ECS Transactions */}
                <Panel header="NACH/ECS Transactions" className="mb-3">
                    {nachTransactions.monthlyDetails ? (
                        <DataTable value={nachTransactions.monthlyDetails} size="small">
                            <Column field="monthYear" header="Month" />
                            <Column field="value" header="Amount" body={(row) => formatCurrency(row.value)} />
                            <Column field="total" header="Count" />
                            <Column header="Status" body={() => <Tag severity="success" value="Cleared" />} />
                        </DataTable>
                    ) : (
                        <p>No NACH/ECS transactions found</p>
                    )}
                </Panel>

                {/* Loan/EMI Transactions */}
                <Panel header="Loan/EMI Transaction Details" className="mb-3">
                    {emiTransactions.length > 0 ? (
                        <DataTable value={emiTransactions} size="small" paginator rows={10}>
                            <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                            <Column field="description" header="Description" />
                            <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                            <Column header="Type" body={(row) => 
                                <Tag severity={row.transactionType === 'DEBIT' ? 'warning' : 'success'} 
                                     value={row.transactionType} />
                            } />
                        </DataTable>
                    ) : (
                        <p>No loan/EMI transactions found</p>
                    )}
                </Panel>

                {/* Bounced Transactions */}
                <Panel header="Bounced Cheques/Transactions" className="mb-3">
                    {bounceTransactions.length === 0 ? (
                        <div className="no-issues">
                            <i className="pi pi-check-circle" style={{ color: 'var(--green-500)', fontSize: '2em' }}></i>
                            <p>No bounced transactions detected</p>
                        </div>
                    ) : (
                        <DataTable value={bounceTransactions} size="small">
                            <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                            <Column field="description" header="Description" />
                            <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                            <Column header="Impact" body={() => <Tag severity="danger" value="Bounced" />} />
                        </DataTable>
                    )}
                </Panel>
                
                {/* EMI Transactions Modal */}
                <Dialog
                    header="All EMI Transactions"
                    visible={showEmiTransactionsModal}
                    onHide={() => setShowEmiTransactionsModal(false)}
                    modal
                    className="w-11 md:w-9 lg:w-8"
                    maximizable
                >
                    <div className="emi-transactions-modal">
                        {emiTransactions.length > 0 ? (
                            <DataTable 
                                value={emiTransactions} 
                                size="small" 
                                paginator 
                                rows={10}
                                scrollable 
                                scrollHeight="400px"
                                className="p-datatable-sm"
                            >
                                <Column 
                                    field="date" 
                                    header="Date" 
                                    body={(row) => new Date(row.date).toLocaleDateString('en-IN')} 
                                    style={{ width: '100px' }}
                                />
                                <Column 
                                    field="description" 
                                    header="Description" 
                                    style={{ minWidth: '300px' }}
                                />
                                <Column 
                                    field="amount" 
                                    header="Amount" 
                                    body={(row) => formatCurrency(row.amount)} 
                                    style={{ width: '120px' }}
                                />
                                <Column 
                                    field="transactionType" 
                                    header="Type" 
                                    body={(row) => 
                                        <Tag severity={row.transactionType === 'DEBIT' ? 'warning' : 'success'} 
                                             value={row.transactionType} />
                                    } 
                                    style={{ width: '100px' }}
                                />
                                <Column 
                                    field="bank" 
                                    header="Bank" 
                                    style={{ width: '150px' }}
                                />
                                <Column 
                                    field="closingBalance" 
                                    header="Closing Balance" 
                                    body={(row) => formatCurrency(row.closingBalance)} 
                                    style={{ width: '150px' }}
                                />
                            </DataTable>
                        ) : (
                            <div className="text-center p-4">
                                <i className="pi pi-info-circle text-3xl text-blue-500 mb-3"></i>
                                <p>No EMI transactions found</p>
                            </div>
                        )}
                    </div>
                </Dialog>
            </div>
        );
    };

    // Tab E: Share Transactions
    const renderShareTransactions = () => {
        const investmentKeywords = ['zerodha', 'groww', 'upstox', 'kite', 'angel', 'iifl', '5paisa', 'sharekhan', 'motilal'];
        const investmentTransactions = transactions.filter(t => 
            investmentKeywords.some(keyword => 
                t.description?.toLowerCase().includes(keyword)
            )
        ) || [];
        
        return (
            <div className="share-transactions-tab">
                <Panel header="Investment/Share Trading Analysis" className="mb-3">
                    <div className="investment-summary">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Investment Activity:</label>
                                <Tag severity={investmentTransactions.length > 0 ? 'info' : 'success'} 
                                     value={investmentTransactions.length > 0 ? 'Yes' : 'No'} />
                            </div>
                            <div className="info-item">
                                <label>Total Investment Transactions:</label>
                                <span>{investmentTransactions.length}</span>
                            </div>
                            <div className="info-item">
                                <label>Total Investment Amount:</label>
                                <span>{formatCurrency(
                                    investmentTransactions
                                        .filter(t => t.transactionType === 'DEBIT')
                                        .reduce((sum, t) => sum + t.amount, 0)
                                )}</span>
                            </div>
                        </div>
                    </div>
                    
                    {investmentTransactions.length > 0 ? (
                        <DataTable value={investmentTransactions} size="small" className="mt-3">
                            <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                            <Column field="description" header="Platform/Description" />
                            <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                            <Column field="transactionType" header="Type" body={(row) => 
                                <Tag severity={row.transactionType === 'CREDIT' ? 'success' : 'warning'} 
                                     value={row.transactionType} />
                            } />
                        </DataTable>
                    ) : (
                        <div className="no-issues mt-3">
                            <i className="pi pi-info-circle" style={{ color: 'var(--blue-500)', fontSize: '2em' }}></i>
                            <p>No investment/share trading transactions detected</p>
                        </div>
                    )}
                </Panel>
            </div>
        );
    };

    // Tab F: Online Betting
    const renderOnlineBetting = () => {
        const bettingKeywords = ['dream11', 'mpl', 'bet365', 'betting', 'fantasy', 'my11circle', 'ballebaazi', 'gamezy'];
        const bettingTransactions = transactions.filter(t => 
            bettingKeywords.some(keyword => 
                t.description?.toLowerCase().includes(keyword)
            )
        ) || [];
        
        return (
            <div className="betting-transactions-tab">
                <Panel header="Online Betting/Gaming Analysis" className="mb-3">
                    <div className="betting-summary">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Betting Activity Detected:</label>
                                <Tag severity={bettingTransactions.length > 0 ? 'danger' : 'success'} 
                                     value={bettingTransactions.length > 0 ? 'Yes' : 'No'} />
                            </div>
                            <div className="info-item">
                                <label>Total Betting Transactions:</label>
                                <span className={bettingTransactions.length > 0 ? 'text-danger' : ''}>
                                    {bettingTransactions.length}
                                </span>
                            </div>
                            <div className="info-item">
                                <label>Total Amount in Betting:</label>
                                <span className={bettingTransactions.length > 0 ? 'text-danger' : ''}>
                                    {formatCurrency(
                                        bettingTransactions
                                            .filter(t => t.transactionType === 'DEBIT')
                                            .reduce((sum, t) => sum + t.amount, 0)
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {bettingTransactions.length > 0 ? (
                        <>
                            <Message severity="warn" text="Online betting transactions detected. This may impact creditworthiness assessment." className="mb-3" />
                            <DataTable value={bettingTransactions} size="small">
                                <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                                <Column field="description" header="Platform" />
                                <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                                <Column header="Risk" body={() => <Tag severity="danger" value="High Risk" />} />
                            </DataTable>
                        </>
                    ) : (
                        <div className="no-issues mt-3">
                            <i className="pi pi-check-circle" style={{ color: 'var(--green-500)', fontSize: '2em' }}></i>
                            <p>No online betting transactions detected - Good financial behavior</p>
                        </div>
                    )}
                </Panel>
            </div>
        );
    };

    // Tab G: Fraud Analytics
    const renderFraudAnalytics = () => {
        // Check for duplicate transactions
        const duplicateTransactions = [];
        const transactionMap = new Map();
        
        transactions.forEach(t => {
            const key = `${t.date}_${t.amount}_${t.description}`;
            if (transactionMap.has(key)) {
                duplicateTransactions.push(t);
            } else {
                transactionMap.set(key, t);
            }
        });
        
        // Check for round number deposits (potential fraud indicator)
        const suspiciousDeposits = transactions.filter(t => 
            t.transactionType === 'CREDIT' && 
            t.amount >= 50000 && 
            t.amount % 10000 === 0
        );
        
        return (
            <div className="fraud-analytics-tab">
                <Panel header="Fraud Analytics & Document Verification" className="mb-3">
                    <div className="fraud-summary">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Document Status:</label>
                                <Tag severity="success" value="Verified" />
                            </div>
                            <div className="info-item">
                                <label>Duplicate Transactions:</label>
                                <Tag severity={duplicateTransactions.length > 0 ? 'warning' : 'success'} 
                                     value={duplicateTransactions.length > 0 ? `${duplicateTransactions.length} Found` : 'None'} />
                            </div>
                            <div className="info-item">
                                <label>Suspicious Patterns:</label>
                                <Tag severity={suspiciousDeposits.length > 5 ? 'warning' : 'success'} 
                                     value={suspiciousDeposits.length > 5 ? 'Detected' : 'None'} />
                            </div>
                            <div className="info-item">
                                <label>Overall Risk:</label>
                                <Tag severity="success" value="Low" />
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Duplicate Transactions */}
                <Panel header="Duplicate Transaction Check" className="mb-3">
                    {duplicateTransactions.length === 0 ? (
                        <div className="no-issues">
                            <i className="pi pi-shield" style={{ color: 'var(--green-500)', fontSize: '2em' }}></i>
                            <p>No duplicate transactions detected</p>
                        </div>
                    ) : (
                        <DataTable value={duplicateTransactions} size="small">
                            <Column field="date" header="Date" />
                            <Column field="description" header="Description" />
                            <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                            <Column header="Flag" body={() => <Tag severity="warning" value="Duplicate" />} />
                        </DataTable>
                    )}
                </Panel>

                {/* Pattern Analysis */}
                <Panel header="Transaction Pattern Analysis" className="mb-3">
                    <div className="pattern-analysis">
                        <div className="flex justify-content-between align-items-center">
                            <h5>Round Number Deposits (₹50,000+)</h5>
                            {!hideCredits && serviceDetails && (
                                <span className="text-600">
                                    Credits Deduct: {serviceDetails.credits_required || serviceDetails.credits || 0}
                                </span>
                            )}
                        </div>
                        {suspiciousDeposits.length > 0 ? (
                            <DataTable value={suspiciousDeposits.slice(0, 5)} size="small">
                                <Column field="date" header="Date" />
                                <Column field="description" header="Description" />
                                <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                            </DataTable>
                        ) : (
                            <p>No suspicious round number deposits found</p>
                        )}
                    </div>
                </Panel>
            </div>
        );
    };

    // Combined EFG: Risk Analysis (Share Trading, Betting & Fraud)
    const renderRiskAnalysis = () => {
        return (
            <div className="risk-analysis-tab">
                <TabView renderActiveOnly>
                    <TabPanel header="Share Transactions">
                        {renderShareTransactions()}
                    </TabPanel>
                    <TabPanel header="Online Betting">
                        {renderOnlineBetting()}
                    </TabPanel>
                    <TabPanel header="Fraud Analytics">
                        {renderFraudAnalytics()}
                    </TabPanel>
                </TabView>
            </div>
        );
    };

    // Tab H: Other Analysis
    const renderOtherAnalysis = () => {
        const businessTransactions = transactions.filter(t => 
            t.category === 'BUSINESS' || 
            t.purpose === 'BUSINESS' ||
            t.description?.toLowerCase().includes('business')
        );
        
        const missingMonths = accountSummary.missingMonths || [];
        
        return (
            <div className="other-analysis-tab">
                {/* Business Cash Flow */}
                <Panel header="Business Cash Flow Analysis" className="mb-3">
                    {businessTransactions.length > 0 ? (
                        <div>
                            <div className="info-grid mb-3">
                                <div className="info-item">
                                    <label>Business Transactions:</label>
                                    <span>{businessTransactions.length}</span>
                                </div>
                                <div className="info-item">
                                    <label>Business Credits:</label>
                                    <span>{formatCurrency(
                                        businessTransactions
                                            .filter(t => t.transactionType === 'CREDIT')
                                            .reduce((sum, t) => sum + t.amount, 0)
                                    )}</span>
                                </div>
                                <div className="info-item">
                                    <label>Business Debits:</label>
                                    <span>{formatCurrency(
                                        businessTransactions
                                            .filter(t => t.transactionType === 'DEBIT')
                                            .reduce((sum, t) => sum + t.amount, 0)
                                    )}</span>
                                </div>
                            </div>
                            <DataTable value={businessTransactions.slice(0, 10)} size="small">
                                <Column field="date" header="Date" />
                                <Column field="description" header="Description" />
                                <Column field="amount" header="Amount" body={(row) => formatCurrency(row.amount)} />
                                <Column field="transactionType" header="Type" />
                            </DataTable>
                        </div>
                    ) : (
                        <p>No specific business transactions identified</p>
                    )}
                </Panel>

                {/* Document Completeness */}
                <Panel header="Document Completeness Check" className="mb-3">
                    <div className="document-check">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Bank Statement:</label>
                                <Tag severity="success" value="Available" />
                            </div>
                            <div className="info-item">
                                <label>Missing Months:</label>
                                <Tag severity={missingMonths.length > 0 ? 'warning' : 'success'} 
                                     value={missingMonths.length > 0 ? missingMonths.join(', ') : 'None'} />
                            </div>
                            <div className="info-item">
                                <label>Statement Period:</label>
                                <Tag severity="info" value={`${accountSummary.openingAndClosingBalance?.length || 0} months`} />
                            </div>
                            <div className="info-item">
                                <label>Data Quality:</label>
                                <Tag severity="success" value="Complete" />
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Additional Insights */}
                <Panel header="Additional Financial Insights" className="mb-3">
                    <div className="additional-insights">
                        <div className="info-grid">
                            <div className="info-item">
                                <label>Cash Deposits:</label>
                                <span>{formatCurrency(monthWiseAnalysis.cashDeposit?.totalValue || 0)}</span>
                            </div>
                            <div className="info-item">
                                <label>Cash Withdrawals:</label>
                                <span>{formatCurrency(monthWiseAnalysis.cashWithdrawals?.totalValue || 0)}</span>
                            </div>
                            <div className="info-item">
                                <label>Cheque Deposits:</label>
                                <span>{formatCurrency(monthWiseAnalysis.chqDeposit?.totalValue || 0)}</span>
                            </div>
                            <div className="info-item">
                                <label>Cheque Issues:</label>
                                <span>{formatCurrency(monthWiseAnalysis.chqIssues?.totalValue || 0)}</span>
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>
        );
    };

    // Helper function to render daily balance chart
    const renderDailyBalanceChart = (balanceData) => {
        if (!balanceData || balanceData.length === 0) return <p>No balance data available</p>;
        
        const chartData = {
            labels: balanceData.map(d => d.monthAndYear),
            datasets: [
                {
                    label: 'Opening Balance',
                    data: balanceData.map(d => d.openingBalance),
                    borderColor: 'var(--blue-500)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Closing Balance',
                    data: balanceData.map(d => d.closingBalance),
                    borderColor: 'var(--green-500)',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    tension: 0.4
                }
            ]
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top'
                }
            },
            scales: {
                y: {
                    ticks: {
                        callback: (value) => '₹' + (value / 100000).toFixed(1) + 'L'
                    }
                }
            }
        };

        return (
            <div style={{ height: '300px' }}>
                <Chart type="line" data={chartData} options={chartOptions} />
            </div>
        );
    };

    // Render detail content based on selected card
    const renderDetailContent = () => {
        if (!selectedCard) return null;
        
        switch (selectedCard.id) {
            case 'A':
                return renderTransactionAnalysis();
            case 'B':
                return renderSalaryTransactions();
            case 'C':
                return renderNewLoanEMIAnalysis();
            case 'D':
                return renderLoanTransactions();
            case 'EFG':
                return renderRiskAnalysis();
            case 'H':
                return renderOtherAnalysis();
            default:
                return null;
        }
    };

    // Render Charts Section
    const renderChartsSection = () => {
        if (!accountSummary || !monthWiseAnalysis) return null;

        // Prepare monthly balance trend data
        const monthlyBalance = monthWiseAnalysis.monthlyAvgBal?.monthlyDetails || [];
        const monthlyMaxBalance = monthWiseAnalysis.maxBalance?.monthlyDetails || [];
        const monthlyMinBalance = monthWiseAnalysis.minBalance?.monthlyDetails || [];

        const balanceChartData = {
            labels: monthlyBalance.map(item => item.monthYear),
            datasets: [
                {
                    label: 'Average Balance',
                    data: monthlyBalance.map(item => item.value),
                    borderColor: 'var(--blue-500)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Max Balance',
                    data: monthlyMaxBalance.map(item => item.value),
                    borderColor: 'var(--green-500)',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    tension: 0.4,
                    fill: false
                },
                {
                    label: 'Min Balance',
                    data: monthlyMinBalance.map(item => item.value),
                    borderColor: 'var(--red-500)',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    tension: 0.4,
                    fill: false
                }
            ]
        };

        // Prepare category breakdown data
        const categoryData = {
            labels: ['Salary', 'Loan Credits', 'Cash Deposits', 'Digital Transfer', 'Others'],
            datasets: [{
                data: [
                    monthWiseAnalysis.salary?.totalValue || 0,
                    monthWiseAnalysis.loanCredit?.totalValue || 0,
                    monthWiseAnalysis.cashDeposit?.totalValue || 0,
                    monthWiseAnalysis.digitalTransfer?.digitalTransferCredit?.totalValue || 0,
                    monthWiseAnalysis.nonSalaryCredit?.totalValue || 0
                ],
                backgroundColor: ['var(--blue-500)', 'var(--purple-500)', 'var(--green-500)', 'var(--orange-500)', 'var(--red-500)']
            }]
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom'
                }
            },
            scales: {
                y: {
                    ticks: {
                        callback: (value) => '₹' + (value / 100000).toFixed(1) + 'L'
                    }
                }
            }
        };

        const pieChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'right'
                }
            }
        };

        return (
            <div className="grid mt-4">
                <div className="col-12 lg:col-8">
                    <Card title="Monthly Balance Trend">
                        <Chart type="line" data={balanceChartData} options={chartOptions} style={{ height: '350px' }} />
                    </Card>
                </div>
                <div className="col-12 lg:col-4">
                    <Card title="Income Category Breakdown">
                        <Chart type="pie" data={categoryData} options={pieChartOptions} style={{ height: '350px' }} />
                    </Card>
                </div>
            </div>
        );
    };

    // Render Comprehensive Monthly Metrics
    const renderComprehensiveMonthlyMetrics = () => {
        if (!monthWiseAnalysis) return null;

        const allMetrics = Object.keys(monthWiseAnalysis).map(key => {
            const metric = monthWiseAnalysis[key];
            if (!metric || typeof metric !== 'object') return null;

            const { label, colorClass } = getMetricConfig(key);
            
            // Calculate max value from monthlyDetails if not provided
            let maxValue = metric.max || 0;
            if (maxValue === 0 && metric.monthlyDetails && Array.isArray(metric.monthlyDetails)) {
                const values = metric.monthlyDetails.map(item => item.value || 0);
                if (values.length > 0) {
                    maxValue = Math.max(...values);
                }
            }
            
            return (
                <div key={key} className="col-12 md:col-6 lg:col-4 mb-3">
                    <Card>
                        <div className="flex justify-content-between align-items-center mb-3">
                            <h5 className={`${colorClass} m-0`}>{label}</h5>
                        </div>
                        <div className="grid">
                            <div className="col-6">
                                <p className="text-600 mb-1">Total</p>
                                <p className="font-bold text-xl">{formatCurrency(metric.totalValue || 0)}</p>
                            </div>
                            <div className="col-6">
                                <p className="text-600 mb-1">Average</p>
                                <p className="font-bold text-xl">{formatCurrency(metric.average || 0)}</p>
                            </div>
                            <div className="col-6">
                                <p className="text-600 mb-1">Count</p>
                                <p className="font-bold">{metric.total || 0}</p>
                            </div>
                            <div className="col-6">
                                <p className="text-600 mb-1">Max</p>
                                <p className="font-bold">{formatCurrency(maxValue)}</p>
                            </div>
                        </div>
                    </Card>
                </div>
            );
        }).filter(Boolean);

        return <div className="grid">{allMetrics}</div>;
    };

    // Get metric configuration
    const getMetricConfig = (key) => {
        const metrics = {
            credits: { label: 'Total Credits', colorClass: 'text-green-600' },
            debits: { label: 'Total Debits', colorClass: 'text-red-600' },
            monthlyAvgBal: { label: 'Monthly Average Balance', colorClass: 'text-blue-600' },
            salary: { label: 'Salary', colorClass: 'text-green-600' },
            loanCredit: { label: 'Loan Credits', colorClass: 'text-purple-600' },
            cashDeposit: { label: 'Cash Deposits', colorClass: 'text-teal-600' },
            cashWithdrawals: { label: 'Cash Withdrawals', colorClass: 'text-orange-600' },
            creditCardPayment: { label: 'Credit Card Payments', colorClass: 'text-pink-600' },
            'ECS/NACH': { label: 'ECS/NACH', colorClass: 'text-indigo-600' },
            bankCharges: { label: 'Bank Charges', colorClass: 'text-red-600' }
        };
        
        return metrics[key] || { label: key, colorClass: 'text-gray-600' };
    };

    // Render Monthly Analysis Table
    const renderMonthlyAnalysisTable = () => {
        if (!monthWiseAnalysis) return null;

        const monthlyData = [];
        const monthlyAvgBal = monthWiseAnalysis.monthlyAvgBal?.monthlyDetails || [];
        const monthlyCredits = monthWiseAnalysis.credits?.monthlyDetails || [];
        const monthlyDebits = monthWiseAnalysis.debits?.monthlyDetails || [];

        // Combine data by month
        monthlyAvgBal.forEach(item => {
            const credit = monthlyCredits.find(c => c.monthYear === item.monthYear);
            const debit = monthlyDebits.find(d => d.monthYear === item.monthYear);
            
            monthlyData.push({
                month: item.monthYear,
                avgBalance: item.value,
                credits: credit?.value || 0,
                creditCount: credit?.total || 0,
                debits: debit?.value || 0,
                debitCount: debit?.total || 0,
                netFlow: (credit?.value || 0) - (debit?.value || 0)
            });
        });

        return (
            <Card title="Monthly Financial Summary">
                <DataTable value={monthlyData} className="p-datatable-sm" paginator rows={10}>
                    <Column field="month" header="Month" />
                    <Column header="Average Balance" body={(row) => formatCurrency(row.avgBalance)} />
                    <Column header="Total Credits" body={(row) => `${formatCurrency(row.credits)} (${row.creditCount})`} />
                    <Column header="Total Debits" body={(row) => `${formatCurrency(row.debits)} (${row.debitCount})`} />
                    <Column header="Net Flow" body={(row) => (
                        <Tag 
                            value={formatCurrency(Math.abs(row.netFlow))}
                            severity={row.netFlow >= 0 ? 'success' : 'danger'}
                            icon={row.netFlow >= 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'}
                        />
                    )} />
                </DataTable>
            </Card>
        );
    };

    // Render Transactions Table with additional columns and enhanced filtering
    const renderTransactionsTable = () => {
        if (!transactions || transactions.length === 0) return null;
        
        // Get unique values for dropdowns
        const types = [...new Set(transactions.map(t => t.type))].map(type => ({ label: type, value: type }));
        const subcategories = [...new Set(transactions.map(t => t.transactionSubCategory))]
            .filter(Boolean)
            .map(cat => ({ label: cat, value: cat }));

        const header = (
            <div className="flex justify-content-between align-items-center">
                <h3 className="m-0">All Transactions</h3>
                <div className="flex gap-2">
                    <span className="p-input-icon-left">
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            placeholder="Search party name..."
                        />
                    </span>
                    <Dropdown
                        value={typeFilter}
                        options={types}
                        onChange={(e) => setTypeFilter(e.value)}
                        placeholder="Filter by Type"
                        showClear
                        className="w-12rem"
                    />
                    <Dropdown
                        value={subcategoryFilter}
                        options={subcategories}
                        onChange={(e) => setSubcategoryFilter(e.value)}
                        placeholder="Filter by Subcategory"
                        showClear
                        className="w-12rem"
                    />
                </div>
            </div>
        );

        // Apply filters
        let filteredTransactions = [...transactions];
        
        if (globalFilter) {
            filteredTransactions = filteredTransactions.filter(t => 
                t.partyName?.toLowerCase().includes(globalFilter.toLowerCase())
            );
        }
        
        if (typeFilter) {
            filteredTransactions = filteredTransactions.filter(t => t.type === typeFilter);
        }
        
        if (subcategoryFilter) {
            filteredTransactions = filteredTransactions.filter(t => t.transactionSubCategory === subcategoryFilter);
        }

        return (
            <Card header={header}>
                <DataTable 
                    value={filteredTransactions} 
                    paginator 
                    rows={20}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    className="p-datatable-sm"
                    emptyMessage="No transactions found"
                >
                    <Column 
                        field="dateTime" 
                        header="Date" 
                        body={(row) => new Date(row.dateTime).toLocaleDateString('en-IN')}
                        sortable
                    />
                    <Column 
                        field="description" 
                        header="Description"
                        style={{ maxWidth: '200px' }}
                    />
                    <Column 
                        field="partyName" 
                        header="Party Name"
                        body={(row) => row.partyName || '-'}
                        sortable
                    />
                    <Column 
                        field="amount" 
                        header="Amount" 
                        body={(row) => (
                            <span className={row.type === 'CREDIT' ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                {row.type === 'CREDIT' ? '+' : '-'}{formatCurrency(row.amount)}
                            </span>
                        )}
                        sortable
                    />
                    <Column 
                        field="type" 
                        header="Type"
                        body={(row) => (
                            <Tag severity={row.type === 'CREDIT' ? 'success' : 'danger'} value={row.type} />
                        )}
                        sortable
                    />
                    <Column 
                        field="transactionSubCategory" 
                        header="Subcategory"
                        body={(row) => (
                            <Tag severity="info" value={row.transactionSubCategory || 'OTHER'} />
                        )}
                        sortable
                    />
                    <Column 
                        field="bank" 
                        header="Bank"
                        body={(row) => row.bank || '-'}
                    />
                    <Column 
                        field="balanceAfterTransaction" 
                        header="Balance After"
                        body={(row) => formatCurrency(row.balanceAfterTransaction)}
                        sortable
                    />
                </DataTable>
            </Card>
        );
    };

    // Render Daily Balances with Graph and Pagination
    const renderDailyBalances = () => {
        const rowsPerPage = 10;
        const dailyBalancesData = accountSummary.dailyEODBalance || {};
        const openingClosingBalance = accountSummary.openingAndClosingBalance || [];

        // Group balances by month
        const balancesByMonth = {};
        
        // Process daily balances if available (it's an object with month keys)
        if (typeof dailyBalancesData === 'object' && Object.keys(dailyBalancesData).length > 0) {
            Object.entries(dailyBalancesData).forEach(([monthName, dayData]) => {
                if (!balancesByMonth[monthName]) {
                    balancesByMonth[monthName] = {
                        monthName,
                        balances: []
                    };
                }
                
                // Process each day's data
                Object.entries(dayData).forEach(([day, balanceArray]) => {
                    if (Array.isArray(balanceArray) && balanceArray.length > 0) {
                        const balance = balanceArray[0]; // Take the first balance entry for the day
                        balancesByMonth[monthName].balances.push({
                            date: `${day} ${monthName}`,
                            day: parseInt(day),
                            openingBalance: balance.openingBalance || 0,
                            closingBalance: balance.closingBalance || 0,
                            transactions: balance.transactions || 0
                        });
                    }
                });
                
                // Sort balances by day
                balancesByMonth[monthName].balances.sort((a, b) => a.day - b.day);
            });
        } else if (Array.isArray(openingClosingBalance) && openingClosingBalance.length > 0) {
            // Fall back to opening/closing balance data
            openingClosingBalance.forEach(balance => {
                const monthName = balance.monthAndYear || balance.month || 'Unknown';
                const monthKey = monthName;
                
                if (!balancesByMonth[monthKey]) {
                    balancesByMonth[monthKey] = {
                        monthName,
                        balances: []
                    };
                }
                
                balancesByMonth[monthKey].balances.push({
                    date: monthName,
                    day: 1,
                    openingBalance: balance.openingBalance || 0,
                    closingBalance: balance.closingBalance || 0,
                    transactions: 0
                });
            });
        }

        if (Object.keys(balancesByMonth).length === 0) return null;

        return (
            <Card title="Daily End-of-Day Balances">
                <Accordion multiple>
                    {Object.entries(balancesByMonth).map(([monthKey, monthData]) => {
                        const monthClosingBalances = monthData.balances.map(b => b.closingBalance);
                        const avgBalance = monthClosingBalances.reduce((a, b) => a + b, 0) / monthClosingBalances.length;
                        const maxBalance = Math.max(...monthClosingBalances);
                        const minBalance = Math.min(...monthClosingBalances);

                        // Prepare chart data for the month
                        const chartData = {
                            labels: monthData.balances.map(b => b.day.toString()),
                            datasets: [
                                {
                                    label: 'Closing Balance',
                                    data: monthData.balances.map(b => b.closingBalance),
                                    borderColor: 'var(--blue-500)',
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                    tension: 0.4,
                                    fill: true
                                },
                                {
                                    label: 'Opening Balance',
                                    data: monthData.balances.map(b => b.openingBalance),
                                    borderColor: 'var(--green-500)',
                                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                    tension: 0.4,
                                    fill: false,
                                    borderDash: [5, 5]
                                }
                            ]
                        };

                        const chartOptions = {
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: {
                                    position: 'top'
                                },
                                tooltip: {
                                    callbacks: {
                                        label: (context) => {
                                            const label = context.dataset.label || '';
                                            const value = formatCurrency(context.parsed.y);
                                            return `${label}: ${value}`;
                                        }
                                    }
                                }
                            },
                            scales: {
                                y: {
                                    ticks: {
                                        callback: (value) => '₹' + (value / 100000).toFixed(1) + 'L'
                                    }
                                },
                                x: {
                                    title: {
                                        display: true,
                                        text: 'Day of Month'
                                    }
                                }
                            }
                        };

                        return (
                            <AccordionTab
                                key={monthKey}
                                header={
                                    <div className="flex justify-content-between align-items-center w-full">
                                        <span className="font-semibold">{monthData.monthName}</span>
                                        <div className="flex align-items-center gap-3">
                                            <Tag value={`Avg: ${formatCurrency(avgBalance)}`} severity="info" />
                                            <Tag value={`Max: ${formatCurrency(maxBalance)}`} severity="success" />
                                            <Tag value={`Min: ${formatCurrency(minBalance)}`} severity="warning" />
                                        </div>
                                    </div>
                                }
                            >
                                <div className="grid">
                                    <div className="col-12 lg:col-6">
                                        <Card title="Daily Balance Trend">
                                            <Chart type="line" data={chartData} options={chartOptions} style={{ height: '300px' }} />
                                        </Card>
                                    </div>
                                    <div className="col-12 lg:col-6">
                                        <Card title="Daily Balance Details">
                                            <DataTable 
                                                value={monthData.balances} 
                                                className="p-datatable-sm"
                                                paginator
                                                rows={10}
                                                first={currentPage * rowsPerPage}
                                                onPage={(e) => setCurrentPage(e.page)}
                                            >
                                                <Column field="date" header="Date" />
                                                <Column field="openingBalance" header="Opening" body={(row) => formatCurrency(row.openingBalance)} />
                                                <Column field="closingBalance" header="Closing" body={(row) => formatCurrency(row.closingBalance)} />
                                                <Column header="Change" body={(row) => {
                                                    const change = row.closingBalance - row.openingBalance;
                                                    return (
                                                        <span className={change >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                                                            {change >= 0 ? '+' : ''}{formatCurrency(change)}
                                                        </span>
                                                    );
                                                }} />
                                            </DataTable>
                                        </Card>
                                    </div>
                                </div>
                            </AccordionTab>
                        );
                    })}
                </Accordion>
            </Card>
        );
    };

    // Render Income/Expense Insights with Enhanced Graph and Details
    const renderIncomeExpenseInsights = () => {
        const incomeData = accountSummary.incomePerMonth || [];
        const expenseData = accountSummary.expensePerMonth || [];
        const eodBalanceData = accountSummary.eodBalancePerMonth || [];
        const incomes = accountSummary.incomes || [];
        const expenses = accountSummary.expenses || [];

        if (incomeData.length === 0 && expenseData.length === 0) return null;

        // Prepare timeline data
        const monthlyInsights = {};
        
        incomeData.forEach(item => {
            const monthKey = new Date(item.date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
            if (!monthlyInsights[monthKey]) {
                monthlyInsights[monthKey] = { income: 0, expense: 0, balance: 0, netFlow: 0 };
            }
            monthlyInsights[monthKey].income = item.amount;
        });

        expenseData.forEach(item => {
            const monthKey = new Date(item.date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
            if (!monthlyInsights[monthKey]) {
                monthlyInsights[monthKey] = { income: 0, expense: 0, balance: 0, netFlow: 0 };
            }
            monthlyInsights[monthKey].expense = item.amount;
        });

        eodBalanceData.forEach(item => {
            const monthKey = new Date(item.date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
            if (monthlyInsights[monthKey]) {
                monthlyInsights[monthKey].balance = item.amount;
            }
        });

        // Calculate net flow
        Object.keys(monthlyInsights).forEach(key => {
            monthlyInsights[key].netFlow = monthlyInsights[key].income - monthlyInsights[key].expense;
        });

        // Bar chart data
        const barChartData = {
            labels: Object.keys(monthlyInsights),
            datasets: [
                {
                    label: 'Income',
                    data: Object.values(monthlyInsights).map(d => d.income),
                    backgroundColor: 'rgba(16, 185, 129, 0.8)',
                    borderColor: 'var(--green-500)',
                    borderWidth: 1
                },
                {
                    label: 'Expense',
                    data: Object.values(monthlyInsights).map(d => d.expense),
                    backgroundColor: 'rgba(239, 68, 68, 0.8)',
                    borderColor: 'var(--red-500)',
                    borderWidth: 1
                }
            ]
        };

        // Line chart data for net flow
        const lineChartData = {
            labels: Object.keys(monthlyInsights),
            datasets: [
                {
                    label: 'Net Cash Flow',
                    data: Object.values(monthlyInsights).map(d => d.netFlow),
                    borderColor: d => d.y >= 0 ? 'var(--green-500)' : 'var(--red-500)',
                    backgroundColor: d => d.y >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    tension: 0.4,
                    fill: true
                },
                {
                    label: 'Balance',
                    data: Object.values(monthlyInsights).map(d => d.balance),
                    borderColor: 'var(--blue-500)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: false,
                    yAxisID: 'y1'
                }
            ]
        };

        const chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top'
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const label = context.dataset.label || '';
                            const value = formatCurrency(context.parsed.y);
                            return `${label}: ${value}`;
                        }
                    }
                }
            },
            scales: {
                y: {
                    ticks: {
                        callback: (value) => '₹' + (value / 1000).toFixed(0) + 'K'
                    }
                },
                y1: {
                    type: 'linear',
                    display: true,
                    position: 'right',
                    grid: {
                        drawOnChartArea: false
                    },
                    ticks: {
                        callback: (value) => '₹' + (value / 100000).toFixed(1) + 'L'
                    }
                }
            }
        };

        // Get category breakdown
        const categoryBreakdown = {};
        expenses.forEach(exp => {
            const cat = exp.category || 'OTHER';
            if (!categoryBreakdown[cat]) {
                categoryBreakdown[cat] = { amount: 0, count: 0 };
            }
            categoryBreakdown[cat].amount += exp.amount;
            categoryBreakdown[cat].count++;
        });

        const pieChartData = {
            labels: Object.keys(categoryBreakdown),
            datasets: [{
                data: Object.values(categoryBreakdown).map(d => d.amount),
                backgroundColor: [
                    'var(--blue-500)', 'var(--purple-500)', 'var(--green-500)', 'var(--orange-500)', 'var(--red-500)',
                    'var(--pink-500)', 'var(--teal-500)', 'var(--indigo-500)', 'var(--lime-500)', 'var(--orange-600)'
                ]
            }]
        };

        return (
            <div className="income-expense-insights">
                <div className="grid">
                    <div className="col-12 lg:col-6">
                        <Card title="Monthly Income vs Expense">
                            <Chart type="bar" data={barChartData} options={chartOptions} style={{ height: '350px' }} />
                        </Card>
                    </div>
                    <div className="col-12 lg:col-6">
                        <Card title="Cash Flow & Balance Trend">
                            <Chart type="line" data={lineChartData} options={chartOptions} style={{ height: '350px' }} />
                        </Card>
                    </div>
                    <div className="col-12 lg:col-4">
                        <Card title="Expense Category Breakdown">
                            <Chart type="doughnut" data={pieChartData} style={{ height: '300px' }} />
                        </Card>
                    </div>
                    <div className="col-12 lg:col-8">
                        <Card title="Income & Expense Details">
                            <TabView renderActiveOnly>
                                <TabPanel header="Top Income Sources">
                                    <DataTable value={incomes.slice(0, 10)} className="p-datatable-sm">
                                        <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                                        <Column field="description" header="Description" />
                                        <Column field="amount" header="Amount" body={(row) => (
                                            <span className="text-green-600 font-semibold">{formatCurrency(row.amount)}</span>
                                        )} sortable />
                                        <Column field="category" header="Category" body={(row) => (
                                            <Tag value={row.category || 'OTHER'} severity="success" />
                                        )} />
                                    </DataTable>
                                </TabPanel>
                                <TabPanel header="Major Expenses">
                                    <DataTable value={expenses.sort((a, b) => b.amount - a.amount).slice(0, 10)} className="p-datatable-sm">
                                        <Column field="date" header="Date" body={(row) => new Date(row.date).toLocaleDateString('en-IN')} />
                                        <Column field="description" header="Description" />
                                        <Column field="amount" header="Amount" body={(row) => (
                                            <span className="text-red-600 font-semibold">{formatCurrency(row.amount)}</span>
                                        )} sortable />
                                        <Column field="category" header="Category" body={(row) => (
                                            <Tag value={row.category || 'OTHER'} severity="danger" />
                                        )} />
                                    </DataTable>
                                </TabPanel>
                                <TabPanel header="Summary Statistics">
                                    <div className="grid">
                                        <div className="col-12 md:col-6">
                                            <div className="flex justify-content-between align-items-center">
                                                <h5>Income Statistics</h5>
                                                {!hideCredits && serviceDetails && (
                                                    <span className="text-600">
                                                        Credits Deduct: {serviceDetails.credits_required || serviceDetails.credits || 0}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Total Income:</label>
                                                <span className="font-bold text-green-600">{formatCurrency(incomes.reduce((sum, i) => sum + i.amount, 0))}</span>
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Average Income:</label>
                                                <span className="font-bold">{formatCurrency(incomes.reduce((sum, i) => sum + i.amount, 0) / (incomes.length || 1))}</span>
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Income Transactions:</label>
                                                <span className="font-bold">{incomes.length}</span>
                                            </div>
                                        </div>
                                        <div className="col-12 md:col-6">
                                            <div className="flex justify-content-between align-items-center">
                                                <h5>Expense Statistics</h5>
                                                {!hideCredits && serviceDetails && (
                                                    <span className="text-600">
                                                        Credits Deduct: {serviceDetails.credits_required || serviceDetails.credits || 0}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Total Expenses:</label>
                                                <span className="font-bold text-red-600">{formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0))}</span>
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Average Expense:</label>
                                                <span className="font-bold">{formatCurrency(expenses.reduce((sum, e) => sum + e.amount, 0) / (expenses.length || 1))}</span>
                                            </div>
                                            <div className="stat-item mb-2">
                                                <label>Expense Transactions:</label>
                                                <span className="font-bold">{expenses.length}</span>
                                            </div>
                                        </div>
                                    </div>
                                </TabPanel>
                            </TabView>
                        </Card>
                    </div>
                </div>
            </div>
        );
    };

    // Render Bank Account Details
    const renderBankAccountDetails = () => {
        const bankAccount = accountSummary.bank_account;
        if (!bankAccount) return null;

        return (
            <Card title="Bank Account Information">
                <div className="grid">
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Account Name</p>
                            <p className="font-semibold text-lg">{bankAccount.accountName}</p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Account Number</p>
                            <p className="font-semibold text-lg">{bankAccount.accountNumber}</p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Bank Name</p>
                            <p className="font-semibold text-lg">{bankAccount.bankName}</p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Account Type</p>
                            <Tag value={bankAccount.accountType} severity="info" />
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Current Balance</p>
                            <p className="font-semibold text-xl text-green-600">
                                {formatCurrency(bankAccount.currentBalance)}
                            </p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">IFSC Code</p>
                            <p className="font-semibold">{bankAccount.IFSC}</p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Email</p>
                            <p className="font-semibold">{bankAccount.email || 'N/A'}</p>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col-3">
                        <div className="mb-3">
                            <p className="text-600 mb-1">Statement Period</p>
                            <p className="font-semibold">{bankAccount.statementUpload}</p>
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    // Render Fraud Analytics Tab
    const renderFraudAnalyticsTab = () => {
        const fraudData = accountSummary.FraudAnalytics;
        if (!fraudData) return null;

        const status = fraudData.fraudAnalyticStatus;
        const results = fraudData.result || [];

        return (
            <Card title="Fraud Analytics">
                <div className="mb-4">
                    <Tag 
                        value={status?.status || 'N/A'} 
                        severity={status?.status === 'NORMAL' ? 'success' : 'danger'}
                        className="text-lg p-2"
                    />
                </div>
                {results.length > 0 && (
                    <DataTable value={results} paginator rows={10} className="p-datatable-sm">
                        <Column field="ruleId" header="Rule ID" />
                        <Column field="rule" header="Rule Code" />
                        <Column field="ruleName" header="Rule Name" />
                        <Column field="category" header="Category" />
                        <Column 
                            field="status" 
                            header="Status" 
                            body={(rowData) => (
                                <Tag 
                                    value={rowData.status} 
                                    severity={rowData.status === 'PASS' ? 'success' : rowData.status === 'NOT_APPLICABLE' ? 'info' : 'warning'}
                                />
                            )}
                        />
                    </DataTable>
                )}
            </Card>
        );
    };

    // Render Top Funds
    const renderTopFunds = () => {
        const topTransfer = accountSummary.topFiveFundsTransfer || {};
        const topReceived = accountSummary.topFiveFundsReceived || {};

        const transferData = [];
        const receivedData = [];

        Object.entries(topTransfer).forEach(([month, transfers]) => {
            transfers.forEach(transfer => {
                transferData.push({ ...transfer, month });
            });
        });

        Object.entries(topReceived).forEach(([month, received]) => {
            received.forEach(rec => {
                receivedData.push({ ...rec, month });
            });
        });

        return (
            <div className="grid">
                <div className="col-12 lg:col-6">
                    <Card title="Top 5 Funds Transfers">
                        <DataTable value={transferData} rows={5} className="p-datatable-sm">
                            <Column field="month" header="Month" />
                            <Column 
                                field="amount" 
                                header="Amount" 
                                body={(rowData) => (
                                    <span className="font-semibold text-red-600">
                                        {formatCurrency(rowData.amount)}
                                    </span>
                                )}
                                sortable 
                            />
                            <Column field="description" header="Description" style={{ maxWidth: '200px' }} />
                            <Column field="category" header="Category" />
                        </DataTable>
                    </Card>
                </div>
                <div className="col-12 lg:col-6">
                    <Card title="Top 5 Funds Received">
                        <DataTable value={receivedData} rows={5} className="p-datatable-sm">
                            <Column field="month" header="Month" />
                            <Column 
                                field="amount" 
                                header="Amount" 
                                body={(rowData) => (
                                    <span className="font-semibold text-green-600">
                                        {formatCurrency(rowData.amount)}
                                    </span>
                                )}
                                sortable 
                            />
                            <Column field="description" header="Description" style={{ maxWidth: '200px' }} />
                            <Column field="category" header="Category" />
                        </DataTable>
                    </Card>
                </div>
            </div>
        );
    };

    // Check if we have data
    if (!analysisResults || !accountSummary) {
        return (
            <div className="bank-statement-analysis p-4">
                <Card className="text-center">
                    <i className="pi pi-exclamation-triangle text-4xl text-orange-500 mb-3"></i>
                    <h3>No Analysis Data Available</h3>
                    <p className="text-600">Please upload bank statements to see the analysis results.</p>
                </Card>
            </div>
        );
    }

    // Extract Excel download link
    const getExcelDownloadInfo = () => {
        console.log('=== BANK STATEMENT EXCEL DOWNLOAD DEBUG ===');
        console.log('analysisResults:', analysisResults);
        console.log('analysisResults?.excel_links:', analysisResults?.excel_links);
        console.log('reportData:', reportData);
        console.log('reportData keys:', Object.keys(reportData || {}));

        // Parse reportData if it's a string
        let parsedReportData = reportData;
        if (typeof reportData === 'string') {
            try {
                parsedReportData = JSON.parse(reportData);
                console.log('Parsed reportData from string:', parsedReportData);
            } catch (e) {
                console.error('Failed to parse reportData:', e);
            }
        }

        // Check multiple possible paths for Excel URL - prioritize root level fields
        const excelUrl = parsedReportData?.excel_download_url ||  // Root level (direct fetch)
                        parsedReportData?.excel_url ||           // Alternative root level
                        parsedReportData?.data?.excel_download_url ||
                        parsedReportData?.data?.excel_url ||
                        parsedReportData?.report_json?.excel_download_url ||  // In report_json
                        parsedReportData?.report_json?.excel_url ||
                        parsedReportData?.report_json?.data?.excel_download_url ||
                        parsedReportData?.report_json?.data?.excel_url ||
                        reportData?.excel_download_url ||  // Original reportData
                        reportData?.excel_url ||
                        reportData?.data?.excel_download_url ||
                        reportData?.data?.excel_url ||
                        analysisResults?.excel_download_url ||  // In analysisResults
                        analysisResults?.excel_url ||
                        analysisResults?.data?.excel_download_url ||
                        analysisResults?.data?.excel_url ||
                        analysisResults?.excel_links?.[Object.keys(analysisResults.excel_links || {})[0]]?.url;

        console.log('Final Excel URL found:', excelUrl);
        console.log('=== END EXCEL DOWNLOAD DEBUG ===');

        const excelLinks = analysisResults?.excel_links;
        if (!excelLinks) return { url: excelUrl };

        // Get the first Excel link (there's usually one per account)
        const firstKey = Object.keys(excelLinks)[0];
        return firstKey ? { ...excelLinks[firstKey], url: excelUrl || excelLinks[firstKey]?.url } : { url: excelUrl };
    };

    const excelInfo = getExcelDownloadInfo();

    // Handle download
    const handleDownloadExcel = () => {
        console.log('=== BANK STATEMENT DOWNLOAD CLICKED ===');
        console.log('handleDownloadExcel called, excelInfo:', excelInfo);
        console.log('excelInfo.url:', excelInfo?.url);
        console.log('reportData.excel_download_url:', reportData?.excel_download_url);
        
        if (excelInfo?.url) {
            console.log('Opening Excel URL:', excelInfo.url);
            window.open(excelInfo.url, '_blank');
            toast.success('Excel download started');
        } else {
            console.error('No Excel URL found');
            console.error('Available data paths:');
            console.error('- reportData.excel_download_url:', reportData?.excel_download_url);
            console.error('- reportData.excel_url:', reportData?.excel_url);
            console.error('- analysisResults.excel_links:', analysisResults?.excel_links);
            toast.error('Excel download URL not available');
        }
    };

    return (
        <div className="bank-statement-dashboard">
            <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2 mb-3">
                <h2 className="text-lg font-semibold text-900 m-0">
                    <i className="pi pi-credit-card mr-2 text-primary" aria-hidden="true" />
                    Bank Statement Analysis Report
                </h2>
                {!shouldHideCredits && (
                    <Tag
                        severity="success"
                        icon="pi pi-wallet"
                        value={`Remaining: ${remainingBalance !== undefined ? remainingBalance : effectiveServiceInfo?.available_credits || 'N/A'}`}
                        className="text-sm"
                    />
                )}
            </div>

            {/* Loan Details Summary */}
            <Card className="mb-4">
                <h3 className="mb-3">Loan Application Analysis Dashboard</h3>
                <div className="grid">
                    <div className="col-12 md:col-6 lg:col">
                        <div className="text-center">
                            <div className="text-500 text-sm mb-2">Loan Amount:</div>
                            <div className={`font-semibold text-xl ${loan_amount > 0 ? 'text-900' : 'text-400'}`}>
                                {loan_amount > 0 ? formatCurrency(loan_amount) : (
                                    <span className="flex align-items-center justify-content-center">
                                        <i className="pi pi-minus-circle mr-2"></i>
                                        Not Provided
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col">
                        <div className="text-center">
                            <div className="text-500 text-sm mb-2">Interest Rate:</div>
                            <div className={`font-semibold text-xl ${interest_rate > 0 ? 'text-900' : 'text-400'}`}>
                                {interest_rate > 0 ? `${interest_rate}%` : (
                                    <span className="flex align-items-center justify-content-center">
                                        <i className="pi pi-minus-circle mr-2"></i>
                                        Not Provided
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col">
                        <div className="text-center">
                            <div className="text-500 text-sm mb-2">Period:</div>
                            <div className={`font-semibold text-xl ${period > 0 ? 'text-900' : 'text-400'}`}>
                                {period > 0 ? `${period} months` : (
                                    <span className="flex align-items-center justify-content-center">
                                        <i className="pi pi-minus-circle mr-2"></i>
                                        Not Provided
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 md:col-6 lg:col">
                        <div className="text-center">
                            <div className="text-500 text-sm mb-2">EMI:</div>
                            <div className={`font-semibold text-xl ${emi > 0 ? 'text-primary' : 'text-400'}`}>
                                {emi > 0 ? formatCurrency(emi) : (
                                    <span className="flex align-items-center justify-content-center">
                                        <i className="pi pi-minus-circle mr-2"></i>
                                        Not Calculated
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>


            {/* Dashboard Cards Grid - First 6 Cards */}
            {!showDetailView && (
                <div className="grid" style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {dashboardCards.slice(0, 6).map((card, index) => renderCard(card, index))}
                </div>
            )}

            {/* Detail View Dialog */}
            <Dialog 
                visible={showDetailView} 
                onHide={closeDetailView}
                header={
                    <div className="dialog-header">
                        <span className="dialog-title">
                            {selectedCard?.id}. {selectedCard?.title}
                        </span>
                        {selectedCard?.subtitle && (
                            <div style={{ fontSize: '0.875rem', color: 'var(--text-color-secondary)', marginTop: '0.25rem' }}>
                                {selectedCard.subtitle}
                            </div>
                        )}
                    </div>
                }
                style={{ width: '90vw', maxWidth: '1200px' }}
                maximizable
                modal
                className="analysis-detail-dialog"
            >
                {renderDetailContent()}
            </Dialog>

            {/* Charts Section - After Dashboard Cards */}
            <div className="mb-4">
                {renderChartsSection()}
            </div>

            {/* Tabbed Analysis View */}
            <TabView className="bank-statement-tabs" renderActiveOnly>
                <TabPanel header="Comprehensive Metrics" leftIcon="pi pi-chart-bar mr-2">
                    {renderComprehensiveMonthlyMetrics() || (
                        <Card>
                            <p className="text-center text-600">No monthly metrics data available.</p>
                        </Card>
                    )}
                </TabPanel>
                
                <TabPanel header="Monthly Analysis" leftIcon="pi pi-calendar mr-2">
                    {renderMonthlyAnalysisTable() || (
                        <Card>
                            <p className="text-center text-600">No monthly analysis data available.</p>
                        </Card>
                    )}
                </TabPanel>
                
                <TabPanel header="Transactions" leftIcon="pi pi-list mr-2">
                    {renderTransactionsTable() || (
                        <Card>
                            <p className="text-center text-600">No transaction data available.</p>
                        </Card>
                    )}
                </TabPanel>

                <TabPanel header="Daily Balances" leftIcon="pi pi-chart-line mr-2">
                    {renderDailyBalances() || (
                        <Card>
                            <p className="text-center text-600">No daily balance data available.</p>
                        </Card>
                    )}
                </TabPanel>

                <TabPanel header="Income/Expense Insights" leftIcon="pi pi-wallet mr-2">
                    {renderIncomeExpenseInsights() || (
                        <Card>
                            <p className="text-center text-600">No income/expense data available.</p>
                        </Card>
                    )}
                </TabPanel>

                <TabPanel header="Bank Details" leftIcon="pi pi-building mr-2">
                    {renderBankAccountDetails() || (
                        <Card>
                            <p className="text-center text-600">No bank details available.</p>
                        </Card>
                    )}
                </TabPanel>

                <TabPanel header="Fraud Analytics" leftIcon="pi pi-shield mr-2">
                    {renderFraudAnalyticsTab() || (
                        <Card>
                            <p className="text-center text-600">No fraud analytics data available.</p>
                        </Card>
                    )}
                </TabPanel>

                <TabPanel header="Top Transactions" leftIcon="pi pi-sort-amount-down mr-2">
                    {renderTopFunds() || (
                        <Card>
                            <p className="text-center text-600">No top transaction data available.</p>
                        </Card>
                    )}
                </TabPanel>
            </TabView>
            
            {/* Download PDF Section - matching Driving Licence style */}
            <div className="mt-3" style={{ textAlign: 'center' }}>
                <Button
                    label="Download Bank Statement Report"
                    icon="pi pi-download"
                    onClick={() => {
                        console.log('=== BANK STATEMENT MAIN DOWNLOAD BUTTON CLICKED ===');
                        console.log('Available download options:');
                        console.log('- reportData?.pdf_url:', reportData?.pdf_url);
                        console.log('- reportData?.data?.pdf_url:', reportData?.data?.pdf_url);
                        console.log('- pdfDownloadUrl:', pdfDownloadUrl);
                        console.log('- excelInfo?.url:', excelInfo?.url);
                        console.log('- onDownloadPDF:', typeof onDownloadPDF);
                        
                        const pdfUrl = reportData?.pdf_url || reportData?.data?.pdf_url || pdfDownloadUrl || excelInfo?.url;
                        console.log('Final pdfUrl selected:', pdfUrl);
                        
                        if (pdfUrl) {
                            console.log('Using pdfUrl:', pdfUrl);
                            window.open(pdfUrl, '_blank');
                            toast.success('Download started');
                        } else if (onDownloadPDF) {
                            console.log('Using onDownloadPDF function');
                            onDownloadPDF();
                        } else if (excelInfo?.url) {
                            console.log('Using handleDownloadExcel');
                            handleDownloadExcel();
                        } else {
                            console.error('No download options available');
                            toast.error('Download URL not available');
                        }
                    }}
                    className="p-button-sm p-button-primary"
                    style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem'
                    }}
                />
            </div>
        </div>
    );
};

export default BankStatementOutputComponent;